// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-500 — "Flush browser stored key material" (COB-506's name for it;
// this module and its helpers keep the original `wipe` vocabulary): remove
// ONE account's local state from ONE browser. The logic half; the flow that
// drives it is `components/browser-wipe-modal.tsx`.
//
// What it is NOT, so the scope stays legible:
//   * not account termination — that is `/account/abandon`, which returns
//     the funds and ends the account server-side. This touches nothing on
//     any server; the account, its vaults and its money are unaffected.
//   * not a whole-device wipe — other accounts that have signed in on this
//     browser keep their seeds, their vault rows and their preferences
//     (COB-503 owns the all-accounts variant).
//
// It is a hygiene control for a user moving machines: after it runs, this
// browser is a fresh device for this account, so the next sign-in routes
// `LookupMatchNoSeed → restorePrompt` (`auth-flow.ts`) exactly as a
// never-used browser would.
//
// Everything here is pure or dependency-injected, mirroring
// `clearOrphanSeedForNewAccount` — the other destructive path in this SPA —
// so the ordering and the fail-closed refusals are unit-tested without a
// Worker, a network, or a render.

import type { AccountWipeReport } from '@cobault/key-agent';
import type { KeyAgentClient } from './key-agent-worker-client.js';
import type { AgentKind } from './session-context.js';
import type { SharedVaultSummary, SharedVaultState, VaultSummary } from './api.js';
import {
  forgetVaultsForFingerprint,
  getMyVaults,
  VAULT_STORE_KEY,
} from './vault-store.js';
// The portfolio arithmetic and the AR-21 copy are reused verbatim from the
// surfaces that already own them — the warning must say the same thing here
// as it does on Home and in the create wizard.
import {
  hasClearedFundingGate,
  isInactiveVault,
  summarizeVaults,
  type VaultPortfolioTotals,
} from './pages/home.js';
import {
  isZeroRedundancyQuorum,
  zeroRedundancyHint,
} from './pages/shared-vault-create.js';

// ── localStorage keys the wipe knows about ────────────────────────────

/** Per-vault refund-notice acknowledgement (`vault-detail.tsx`). Unbounded
 *  in number, so the wipe prefix-scans rather than guessing. */
export const VAULT_REFUND_ACK_PREFIX = 'vault-refund-acked-';

/** The Web3Auth SDK's own session record, as v10 wrote it: ONE unprefixed
 *  localStorage row holding the whole session. Still cleared, because a
 *  browser that logged in before the v11 upgrade (COB-495) has one and no
 *  v11 code path ever removes it. */
export const WEB3AUTH_SESSION_KEY = 'auth_store';

/** COB-495 — the v11 layout, which is NOT visible from the code that reads
 *  it. `@web3auth/auth@11.8.2` hands its session to
 *  `@toruslabs/session-manager`, which splits it across FOUR entries keyed
 *  `<base>:<token>` off the same `auth_store` base (the base is our
 *  `_storageBaseKey` default — we pass neither `sessionKey` nor
 *  `sessionNamespace`):
 *
 *    * `auth_store:session_id`    — localStorage
 *    * `auth_store:access_token`  — localStorage
 *    * `auth_store:id_token`      — localStorage
 *    * `auth_store:refresh_token` — a COOKIE, written `Path=/;
 *      SameSite=Strict; Secure; Max-Age=2592000` and deliberately NOT
 *      HttpOnly, so the SDK — and therefore this wipe — can clear it from JS.
 *
 *  The session id alone is redeemable against Web3Auth's session server
 *  within its TTL, which reconstructs the user's private key; the refresh
 *  token mints a fresh session for thirty days. All of it is key-adjacent
 *  material that ordinary logout deliberately keeps (see `web3authLogout`)
 *  and that a wipe must not leave behind — so the wipe sweeps the whole
 *  `auth_store:` namespace by prefix rather than naming the individual
 *  entries, which keeps it correct if a later release adds another. */
export const WEB3AUTH_SESSION_PREFIX = `${WEB3AUTH_SESSION_KEY}:`;

/** The one v11 session entry that is a cookie, not a localStorage row. */
export const WEB3AUTH_REFRESH_TOKEN_COOKIE = `${WEB3AUTH_SESSION_PREFIX}refresh_token`;

/** The bare v10 row and every v11 `auth_store:` entry — the exact set the
 *  wipe owns, applied identically to localStorage keys and to cookie names. */
function isWeb3AuthSessionKey(key: string): boolean {
  return key === WEB3AUTH_SESSION_KEY || key.startsWith(WEB3AUTH_SESSION_PREFIX);
}

// ── Gating ladder — the pure predicates ───────────────────────────────

/**
 * Whether step 1 (passcode re-challenge against the server) applies.
 *
 * Software (passcode) sessions only. A delegated or hardware account has no
 * server-side Argon2id verifier to check a passcode against — `passcodeCheck`
 * on a Web3Auth account answers `web3auth`, and a hardware account has no
 * verifier row at all — so a challenge there could never return `match`. A
 * gate that can only fail is not protection, it is a broken control; those
 * sessions get the same type-it-back confirmation instead (step 4), which is
 * required of every session regardless of backend.
 */
export function wipePasscodeChallengeApplies(agentKind: AgentKind | null): boolean {
  return agentKind === 'software';
}

/**
 * Whether the backup ceremony (final seed reveal + remediation) is offered.
 * Only the software backend has a browser-held seed to reveal; the Web3Auth
 * and hardware backends have nothing this wipe could destroy — the delegated
 * login and the device's own recovery phrase both survive it.
 */
export function wipeSeedCeremonyApplies(agentKind: AgentKind | null): boolean {
  return agentKind === 'software';
}

/** The acknowledgement the user must tick before the confirm step, per
 *  custody model. The software copy is deliberately the onboarding /
 *  key-backup-gate sentence (`SEED_LOSS_ACK_LABEL`, passed in by the caller)
 *  — one sentence for one consequence, wherever it is shown. */
export function wipeAckLabel(agentKind: AgentKind | null, seedLossLabel: string): string {
  switch (agentKind) {
    case 'software':
      return seedLossLabel;
    case 'web3auth':
      return (
        'I understand this browser will forget my Cobault session and vault ' +
        'list, and that my key stays with my sign-in provider — if I lose ' +
        'that login, no one, including Cobault, can recover it for me.'
      );
    case 'hardware':
      return (
        'I understand this browser will forget my vault list and my ' +
        "device's registered wallet policies, and that my key stays on my " +
        'hardware device with its own recovery phrase.'
      );
    default:
      // No committed agent kind ⇒ no acknowledgement can be accurate, and
      // the flow refuses to advance (see `wipeReadyToConfirm`).
      return '';
  }
}

/**
 * Whether the backup gate is satisfied. Fail-closed on every axis: an
 * unknown probe result blocks (as `keyBackupGateBlocks` already defines),
 * an un-ticked acknowledgement blocks, and a session with no committed
 * agent kind blocks outright — there is no custody model to be accurate
 * about, so no ladder step could be honest.
 */
export function wipeReadyToConfirm(
  agentKind: AgentKind | null,
  backupStatusIsConfirmed: boolean,
  acknowledged: boolean,
): boolean {
  if (agentKind === null) return false;
  return backupStatusIsConfirmed && acknowledged;
}

/** Step 4: the abandon-flow type-it-back. Case- and whitespace-insensitive
 *  (the email is normalised everywhere else too); a null session email can
 *  never match, so the control cannot fire on a half-committed session. */
export function wipeConfirmationMatches(
  typed: string,
  accountEmail: string | null,
): boolean {
  if (!accountEmail) return false;
  const wanted = accountEmail.trim().toLowerCase();
  if (wanted.length === 0) return false;
  return typed.trim().toLowerCase() === wanted;
}

// ── Which vaults are this account's ───────────────────────────────────

/**
 * The vault IDs the wipe is allowed to touch: the union of what the server
 * says this account holds and what this browser has recorded locally under
 * this session's fingerprint.
 *
 * Both halves are needed. The server lists are authoritative and current but
 * omit anything the account no longer holds server-side; the local rows
 * catch exactly that residue (a soft-deleted vault whose anchor and index
 * entry are still here). The union is the largest set we can PROVE belongs
 * to this account — and the wipe never touches anything outside it, which is
 * what keeps another account's rows (including a shared vault this account
 * is not in) out of range.
 */
export function resolveAccountVaultIds(input: {
  vaults: readonly Pick<VaultSummary, 'vault_id'>[];
  sharedVaults: readonly Pick<SharedVaultSummary, 'vault_id'>[];
  localVaultIds: readonly string[];
}): string[] {
  const ids = new Set<string>();
  for (const v of input.vaults) if (v.vault_id) ids.add(v.vault_id);
  for (const v of input.sharedVaults) if (v.vault_id) ids.add(v.vault_id);
  for (const id of input.localVaultIds) if (id) ids.add(id);
  return [...ids].sort();
}

/** The local half of {@link resolveAccountVaultIds}, read from
 *  `cobault_vaults` for this session's fingerprint. */
export function localVaultIdsForFingerprint(fingerprint: string): string[] {
  return getMyVaults(fingerprint).map((m) => m.serverVaultId).filter(Boolean);
}

// ── Step 2: what the user stands to be locked out of ──────────────────

/** Shared-vault states where a lost key still matters. A rejected, deleted
 *  or emptied vault holds nothing and needs no quorum, so warning about it
 *  would be noise in a warning that has to be read. */
const LIVE_SHARED_VAULT_STATES: ReadonlySet<SharedVaultState> = new Set<SharedVaultState>([
  'PENDING_INVITES',
  'PROPOSED',
  'ACTIVE',
]);

/** One live `m == n` shared vault, with the AR-21 sentence that applies to
 *  its exact shape. */
export interface ZeroRedundancyVault {
  vaultId: string;
  name: string | null;
  quorumM: number;
  n: number;
  hasPolicy: boolean;
  /** `zeroRedundancyHint` — the policy variant survives exactly one lost key
   *  (allow-listed withdrawals only); the no-policy variant freezes on the
   *  first. Two materially different outcomes, never collapsed into one. */
  warning: string;
}

export interface WipeExposure {
  /** Yield-vault portfolio figures, from the same `summarizeVaults` the
   *  /vaults tile renders. COB-637: of these, this surface renders ONLY
   *  `heldCount` — every non-terminal vault — and never the tile's
   *  `depositedCount`, because a wipe destroys this browser's key material
   *  for vaults whose deposit has not cleared (ACCEPTED / FUNDING) and for
   *  reaccept-pending ones just as much as for funded ones. The tile's
   *  `totalDepositedSats` is NOT the figure printed beside that count —
   *  `heldDepositedSats` below is — and `nextSettlementUnix` is a tile figure
   *  not rendered here at all. */
  totals: VaultPortfolioTotals;
  /** COB-637: contracted principal summed over the HELD vaults whose deposit
   *  has cleared the funding gate, INCLUDING a reaccept-pending one — its BTC
   *  is in the vault whatever is unresolved about its terms, and its key dies
   *  with this browser like any other. This is what the modal prints beside
   *  `totals.heldCount`, so that sentence is one statement about one set: N
   *  vaults held, holding X BTC. A vault that has not cleared the gate
   *  (ACCEPTED / FUNDING) contributes 0 — ACCEPTED holds nothing, and a
   *  FUNDING deposit may be a partial amount this surface cannot know
   *  (`principal_sats` would over-state it) — while still counting toward
   *  `heldCount`, because losing its key still matters. So the figure can
   *  under-state exposure by a confirming deposit, and only by that.
   *  The tile's `totalDepositedSats` drops the reaccept-pending vault, so
   *  printing that here understated the exposure by a whole deposit. */
  heldDepositedSats: number;
  /** Live shared vaults this account co-signs. */
  liveShared: SharedVaultSummary[];
  /** The subset whose quorum is `m == n` (AR-21). */
  zeroRedundancy: ZeroRedundancyVault[];
}

/**
 * Fold the two already-fetched lists into the warning the confirm ladder
 * shows. Pure — no new endpoints, exactly as the assessment found: everything
 * here comes from `listVaults` + `listSharedVaults`.
 */
export function summarizeWipeExposure(
  vaults: readonly VaultSummary[],
  sharedVaults: readonly SharedVaultSummary[],
): WipeExposure {
  const liveShared = sharedVaults.filter((v) => LIVE_SHARED_VAULT_STATES.has(v.state));
  const zeroRedundancy: ZeroRedundancyVault[] = [];
  for (const v of liveShared) {
    if (!isZeroRedundancyQuorum(v.quorum_m, v.n)) continue;
    zeroRedundancy.push({
      vaultId: v.vault_id,
      name: v.name ?? null,
      quorumM: v.quorum_m,
      n: v.n,
      hasPolicy: v.has_policy,
      warning: zeroRedundancyHint(v.n, v.has_policy),
    });
  }
  // COB-637: the BTC figure this surface prints is the held set's, not the
  // tile's — same funding-gate predicate, without the tile's reaccept-pending
  // exclusion, because that exclusion is about disputed terms and not about
  // whether the BTC is there.
  let heldDepositedSats = 0;
  for (const v of vaults) {
    if (isInactiveVault(v) || !hasClearedFundingGate(v)) continue;
    heldDepositedSats += Math.max(0, v.principal_sats);
  }
  return {
    totals: summarizeVaults([...vaults]),
    heldDepositedSats,
    liveShared,
    zeroRedundancy,
  };
}

// ── The browser-storage half of the wipe ──────────────────────────────

export interface LocalWipeReport {
  /** localStorage keys removed outright, sorted. */
  removedKeys: string[];
  /** Cookie names expired, sorted. Only ever the Web3Auth session's — under
   *  v11 one of its four entries is a cookie (`auth_store:refresh_token`,
   *  see {@link WEB3AUTH_SESSION_PREFIX}), so localStorage alone is no
   *  longer the whole of "this browser's stored session". */
  removedCookies: string[];
  /** `cobault_vaults` rows dropped (this fingerprint's). */
  droppedVaultRows: number;
}

/**
 * Expire every Web3Auth session cookie on this origin — under v11 that is
 * `auth_store:refresh_token`, and the prefix sweep keeps it right if the SDK
 * moves another token into a cookie. Returns the names that were present,
 * sorted and deduplicated.
 *
 * The expiry write mirrors the SDK's own `remove()`: the name, `Path=/`
 * (the SDK writes the cookie host-only at the root path, so that is the one
 * that has to be matched), and a dead lifetime — both `Max-Age=0` and an
 * epoch `Expires`, since only the pair covers every browser. `Secure` and
 * `SameSite` are deliberately NOT repeated: they are no part of a cookie's
 * identity, and re-asserting `Secure` would make the deletion itself fail on
 * a non-TLS origin — exactly where it must not.
 */
export function expireWeb3AuthSessionCookies(): string[] {
  const names = new Set<string>();
  for (const part of document.cookie.split(';')) {
    const raw = part.trim();
    if (raw.length === 0) continue;
    const eq = raw.indexOf('=');
    const name = eq === -1 ? raw : raw.slice(0, eq);
    if (isWeb3AuthSessionKey(name)) names.add(name);
  }
  for (const name of names) {
    document.cookie =
      `${name}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  return [...names].sort();
}

/**
 * Remove this account's browser-storage residue.
 *
 * Scoped three different ways, because the three things are scoped three
 * different ways in the first place: `cobault_vaults` rows carry a
 * fingerprint, the refund acknowledgements carry a vault ID, and the
 * Web3Auth session entries are unscoped — so they are removed ONLY on a
 * delegated session, where they are provably this account's. On a software
 * or hardware session they may belong to a different account that logged in
 * on this browser, and destroying that would be exactly the cross-account
 * damage this whole module is built to avoid.
 *
 * That third one is the only reason a cookie is in scope at all: under v11
 * the Web3Auth session is four entries, and the refresh token is a cookie
 * (see {@link WEB3AUTH_SESSION_PREFIX}). Removing three of the four and
 * calling the browser wiped would be worse than not offering the control.
 *
 * Nothing outside those three is touched. The wipe never clears the origin's
 * localStorage or cookies wholesale — anything else there belongs to another
 * account or another app, and is not ours to destroy.
 */
export function wipeAccountLocalStorage(opts: {
  fingerprint: string;
  vaultIds: readonly string[];
  clearWeb3AuthSession: boolean;
}): LocalWipeReport {
  const removedKeys: string[] = [];
  const droppedVaultRows = forgetVaultsForFingerprint(opts.fingerprint);
  if (droppedVaultRows > 0 && localStorage.getItem(VAULT_STORE_KEY) === null) {
    removedKeys.push(VAULT_STORE_KEY);
  }

  // Prefix-scan: the per-vault acknowledgements are unbounded in number, so
  // they are found by enumeration and matched against this account's ids.
  const wanted = new Set(opts.vaultIds);
  const ackKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key === null || !key.startsWith(VAULT_REFUND_ACK_PREFIX)) continue;
    if (!wanted.has(key.slice(VAULT_REFUND_ACK_PREFIX.length))) continue;
    ackKeys.push(key);
  }
  for (const key of ackKeys) {
    localStorage.removeItem(key);
    removedKeys.push(key);
  }

  // Same enumerate-then-remove shape as the acks above: the v11 session is a
  // NAMESPACE (`auth_store:*`), not a single row, and the v10 row may still
  // be sitting alongside it on a browser that logged in before the upgrade.
  const removedCookies: string[] = [];
  if (opts.clearWeb3AuthSession) {
    const sessionKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key === null || !isWeb3AuthSessionKey(key)) continue;
      sessionKeys.push(key);
    }
    for (const key of sessionKeys) {
      localStorage.removeItem(key);
      removedKeys.push(key);
    }
    removedCookies.push(...expireWeb3AuthSessionCookies());
  }

  return {
    removedKeys: removedKeys.sort(),
    removedCookies: removedCookies.sort(),
    droppedVaultRows,
  };
}

// ── Execution ─────────────────────────────────────────────────────────

/** What the flow hands the executor. Injected rather than imported so the
 *  ordering below is testable with fakes, exactly as
 *  `reconnectSameHardwareDevice` is. */
export interface BrowserWipeDeps {
  agent: Pick<KeyAgentClient, 'wipeAccount' | 'lock' | 'terminate'>;
  agentKind: AgentKind;
  /** This session's local-store scope (passcode-derived for software,
   *  identity-pubkey-derived for Web3Auth, device-derived for hardware). */
  fingerprint: string;
  /** From {@link resolveAccountVaultIds} — never a guess. */
  vaultIds: readonly string[];
  /** `web3authLogout` from `web3auth-login.ts`; called only on a delegated
   *  session. Best-effort by contract. */
  web3authLogout: () => Promise<void>;
  /** The session's own `logout()` — revokes server-side, drops the tokens,
   *  and returns the SPA to `unauthenticated`. */
  logout: () => void;
}

export interface BrowserWipeResult {
  agentReport: AccountWipeReport;
  localReport: LocalWipeReport;
}

/**
 * Run the wipe. The ORDER is the design:
 *
 *   1. the key agent's own store first — the only place actual key material
 *      lives, and the one step that is atomic (a single IndexedDB
 *      transaction). If it throws, nothing else has happened yet and the
 *      session is still intact for the user to retry.
 *   2. the delegated-login session next (`web3authLogout`, then the whole
 *      `auth_store` family — the v10 row, the v11 `auth_store:*` rows and
 *      the `auth_store:refresh_token` cookie) — key-ADJACENT material, so it
 *      goes before the merely-descriptive local state.
 *   3. the descriptive local state (`cobault_vaults`, refund acks).
 *   4. only then the teardown: lock, terminate the Worker / release the HID
 *      interface, and log the session out.
 *
 * Most-sensitive-first, so a failure part-way through can never leave key or
 * key-adjacent material behind while claiming the browser was wiped.
 */
export async function executeBrowserWipe(
  deps: BrowserWipeDeps,
): Promise<BrowserWipeResult> {
  const agentReport = await deps.agent.wipeAccount({ vaultIds: [...deps.vaultIds] });

  const delegated = deps.agentKind === 'web3auth';
  if (delegated) {
    // COB-496, rolled into COB-500: this is the ONE path that ends the
    // Web3Auth SDK session. It is best-effort inside `web3authLogout`
    // already; the `auth_store` removals below are what make the outcome
    // unconditional — which is the whole reason they exist, so they track
    // the SDK's storage layout (COB-495 moved it) rather than trusting the
    // SDK's own `logout()` to have cleared all four entries.
    await deps.web3authLogout();
  }

  const localReport = wipeAccountLocalStorage({
    fingerprint: deps.fingerprint,
    vaultIds: deps.vaultIds,
    clearWeb3AuthSession: delegated,
  });

  try {
    await deps.agent.lock();
  } catch {
    // A dead Worker cannot hold key material — never let this mask a
    // completed wipe.
  }
  deps.agent.terminate();
  deps.logout();

  return { agentReport, localReport };
}
